<script setup lang="ts">
import { Distributor, Trainer } from "../../types/PersologOnlineTypes.ts";
import { PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { isValidEmail } from "../../utils/GeneralUtils.ts";
import SubAccountTable from "./SubAccountTable.vue";
import TransferDialog from "./TransferDialog.vue";
import TooltipButton from "../common/TooltipButton.vue";

const { t } = useI18n();

const props = defineProps({
    visibleToggle: {
        type: Boolean,
        default: false,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
});

const emit = defineEmits(["update:visible"]);

const visible = ref(props.visibleToggle);
const transferEmail = ref("");
const showTransferDialog = ref(false);

watch(
    () => visible.value,
    () => {
        emit("update:visible", visible.value);
    },
);

watch(
    () => props.visibleToggle,
    () => {
        visible.value = props.visibleToggle;
    },
);


function toTrainer(email: string): Trainer {
    return {
        email: email,
        language: props.trainer?.language ?? "en",
        firstname: "",
        lastname: "",
        beta: true,
        company: "",
        purchaseLimit: 0,
        recipient: "TRAINER",
        distributor: {
            id: props.trainer?.distributor.id ?? 1,
            company: props.trainer?.distributor.company ?? "",
        } as Distributor,
        id: "0",
    } as Trainer;
}

function openTransferDialog() {
    showTransferDialog.value = true;
}
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        header="Header"
        :style="{ width: '80vw', maxWidth: '1600px', maxHeight: '99vh' }"
        :breakpoints="{ '1199px': '85vw', '575px': '97vw' }"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
    >
        <template #header>
            <div class="inline-flex items-center flex-wrap gap-8">
                <span class="font-bold whitespace-nowrap">
                    {{ t("header.accountSettings.distributor.heading") }} - {{ trainer?.firstname }} {{ trainer?.lastname }}
                </span>
            </div>
        </template>
        <div class="flex flex-wrap mt-8 gap-4 basis-full md:w-1/2">
            <div class="flex items-center px-4">
                <h3 class="mr-2 my-0">{{ t("header.accountSettings.accountLinks.transfer.header") }}</h3>
                <TooltipButton
                    id="distributor-view_btn_transfer"
                    :content="t('header.accountSettings.accountLinks.tooltips.transfer')"
                    background-color="transparent"
                />
            </div>
            <InputGroup>
                <InputText
                    v-model="transferEmail"
                    type="email"
                    :placeholder="t('common.email-address')"
                    @blur="showTransferDialog = false"
                />
                <Button
                    :icon="'pi pi-file-export'"
                    :label="t('buttons.transfer')"
                    :disabled="
                        !isValidEmail(transferEmail) || transferEmail === props.trainer.email || transferEmail === ''
                    "
                    @click="openTransferDialog"
                />
            </InputGroup>
            <TransferDialog
                v-if="showTransferDialog"
                :visible-toggle="showTransferDialog"
                :account="toTrainer(transferEmail)"
                :trainer="props.trainer"
                @update:visible="(isVisible) => (showTransferDialog = isVisible)"
            />
        </div>
        <div class="flex flex-wrap mt-8 gap-4 flex-1">
            <SubAccountTable
                :trainer="trainer"
                :headline="t('header.accountSettings.distributor.heading-sub-accounts')"
            />
        </div>

        <template #footer>
            <div class="flex justify-end gap-2 mt-8">
                <Button
                    id="distributor-view_btn_close"
                    :label="t('buttons.close')"
                    @click="visible = !visible"
                    autofocus
                />
            </div>
        </template>
    </Dialog>
</template>
